import React from 'react';

const INTEGRATIONS_MODAL_NO_SERVICES = {
  btnCaption: 'Got It, Thanks!',
  title: 'Finish Setting Up Your First Service + Integration',
  body: () => (
    <div>
      <p>
        Let&apos;s finish setting up a service + integration!
      </p>
      <p>
        A PagerDuty service will reflect a service in your infrastructure whether that be an
        application, component, or team that you wish to open incidents against.
      </p>
      <p>
        To create a service, click the green
        <em>&nbsp;+New Service&nbsp;</em>
        button and follow the steps displayed.
        You will be prompted to select a tool to integrate with. We have step-by-step integration
        guides for 300+ of the most popular tools:&nbsp;
        <a href="https://www.pagerduty.com/integrations/" target="_blank" rel="noopener noreferrer">
          view them here!
        </a>
        &nbsp;Once you have configured your integration, your data
        will begin flowing into PagerDuty.
      </p>
      <p>
        Tip: You can add multiple integrations to a single service. This allows you to better
        represent the entities you are monitoring, managing and operating.
      </p>
    </div>
  ),
  namespace: 'responderAndOwnerNurtureBar',
  width: '600px'
};

export const INTEGRATIONS_MODAL_WITH_SERVICES = {
  btnCaption: 'Got It, Thanks!',
  title: 'Finish Setting Up Your First Integration',
  body: () => (
    <div>
      <p>
        Let&apos;s finish your first integration!
      </p>
      <p>
        Click the green
        <em>&nbsp;+New Integration&nbsp;</em>
        button and select the name of the tool you wish to integrate. We have step-by-step
        integration walkthroughs for 300+ of the most popular tools:&nbsp;
        <a href="https://www.pagerduty.com/integrations/" target="_blank" rel="noopener noreferrer">
          view them here!
        </a>
      </p>
      <p>
        Once you have added the integration, copy
        the &apos;Integration Key&apos; and follow the&nbsp;
        <a href="https://www.pagerduty.com/integrations/" target="_blank" rel="noopener noreferrer">
          step-by-step walkthrough
        </a>
        &nbsp;to complete the setup. Your data will begin flowing into PagerDuty.
      </p>
      <p>
        Tip: You can add multiple integrations to a single service. This allows you to better
        represent the entities you are monitoring, managing and operating.
      </p>
    </div>
  ),
  namespace: 'responderAndOwnerNurtureBar',
  width: '600px'
};

const DOWNLOAD_APP_MODAL = {
  title: 'Download the PagerDuty Mobile App',
  body: () => (
    <div>
      <p>
        One of the most popular ways to get notified of incidents is through the
        mobile app. With the mobile app, you can:
      </p>
      <ul>
        <li>See when you&apos;re on call and schedule overrides</li>
        <li>Acknowledge and respond to incidents</li>
        <li>Configure your user profile and push notifications</li>
      </ul>
      <p>
        The mobile app is available on the Apple Store and Google Play
      </p>
    </div>
  ),
  customFooter: closeModal => (
    <div>
      <a
        href="https://apps.apple.com/us/app/pagerduty/id594039512"
        target="_blank"
        rel="noopener noreferrer"
        onClick={closeModal}
      >
        <img border="0" alt="Download on the App Store" src="https://files.readme.io/179997a-Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg" />
      </a>
      <a
        href="https://play.google.com/store/apps/details?id=com.pagerduty.android&amp;hl=en_US&amp;pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
        target="_blank"
        rel="noopener noreferrer"
        onClick={closeModal}
      >
        <img border="0" alt="Get it on Google Play" src="https://files.readme.io/357da2b-google-play-badge.png" />
      </a>
    </div>
  ),
  namespace: 'responderAndOwnerNurtureBar',
  width: '600px'
};

const ADD_USERS_MODAL = {
  btnCaption: 'Got It, Thanks!',
  title: 'Start building your team',
  body: () => (
    <div>
      <p>
        Take the lead and spread the on-call load across your team by adding a
        few team members to your account. A team is usually comprised of three
        roles: admins, managers, and responders.
      </p>
      <p>
        Click Add Users to invite your team and start collaborating now.
      </p>
    </div>
  ),
  namespace: 'responderAndOwnerNurtureBar',
  width: '600px'
};

export const CONFIRM_EXIT_MODAL = {
  id: 'confirmExitNurtureModal',
  title: 'Are you sure?',
  body: () => (
    <div>
      <p>
        Once you close your onboarding goals, you can&apos;t get them back.
      </p>
    </div>
  ),
  customFooter: (closeModal, exitNurtureBar) => (
    <div>
      <button
        type="button"
        onClick={exitNurtureBar}
      >
        Yes, close it
      </button>
      <button
        type="button"
        onClick={closeModal}
      >
        No, leave it
      </button>
    </div>
  ),
  namespace: 'responderAndOwnerNurtureBar',
  width: '600px',
};

const CONTACT_METHODS_MODAL = {
  btnCaption: 'Got It, Thanks!',
  title: 'Set up Your User Profile',
  body: () => (
    <div>
      <p>
        Tell us how you want to be contacted when an incident is assigned to you.
      </p>
      <p>
        There are four ways to be contacted: (1)
        <strong>&nbsp;SMS Text,&nbsp;</strong>
        (2)
        <strong>&nbsp;Push Notification,&nbsp;</strong>
        (3)
        <strong>&nbsp;Phone Call&nbsp;</strong>
        and (4)
        <strong>&nbsp;Email</strong>
        . Add them on this page under the
        <em><strong>&nbsp;Contact Information&nbsp;</strong></em>
        section.
      </p>
      <p>
        Once you add your contact information, you can customize how you receive notifications
        for different scenarios in the
        <em><strong>&nbsp;Notification Rules&nbsp;</strong></em>
        section.
      </p>
    </div>
  ),
  namespace: 'responderAndOwnerNurtureBar',
  width: '600px',
};

const MANAGE_ON_CALL_MODAL = {
  btnCaption: 'Got It, Thanks!',
  title: 'Manage Your On-Call Shifts & Schedule Overrides',
  body: () => (
    <div>
      <p>
        On the right side of your User Profile, there is a section called
        <em><strong>&nbsp;My On-Call Schedules&nbsp;</strong></em>
        which lists all of the schedules you are a part of.
      </p>
      <p>
        If you are going to miss an on-call shift and need to get coverage, you can create
        an override by clicking on the schedule name, locating the specific shift and selecting
        <em>&nbsp;Schedule an Override.</em>
      </p>
      <p>
        <strong>Tip:&nbsp;</strong>
        Make sure to configure on-call reminders so you never miss a shift. You will configure
        this on the Notification Rules tab under &ldquo;Before I go on-call
        or off-call&#8230;&rdquo;
      </p>
    </div>
  ),
  namespace: 'responderAndOwnerNurtureBar',
  width: '600px',
};

const ACK_AND_OWN_INCIDENTS_MODAL = {
  btnCaption: 'Got It, Thanks!',
  title: 'Acking & Owning Incidents',
  body: () => (
    <div>
      <p>
        During your on-call shift, you will get notified if an incident is assigned
        to you. When you receive an incident notification:
      </p>
      <ul>
        <li>
          <em><strong>Acknowledge&nbsp;</strong></em>
          the incident to communicate that you are taking ownership.
        </li>
        <li>
          <em><strong>Reassign&nbsp;</strong></em>
          if it belongs to another person or team.
        </li>
        <li>
          <em><strong>Resolve&nbsp;</strong></em>
          once the issue has been fixed and service is fully restored.
        </li>
      </ul>
      <p>
        Tip: You can acknowledge and respond to incidents faster in the&nbsp;
        <a href="https://www.pagerduty.com/platform/on-call-management/mobile/" target="_blank" rel="noopener noreferrer">
          mobile app!
        </a>
      </p>
    </div>
  ),
  namespace: 'responderAndOwnerNurtureBar',
  width: '600px',
};

const NURTURE_STEPS = {
  integration: {
    id: 'integration',
    text: 'Set up your first integration',
    url: '/services',
    audienceKey: '',
    completed: false,
    modal: INTEGRATIONS_MODAL_NO_SERVICES,
    buttonText: 'Set it Up Now',
  },
  downloadApp: {
    id: 'downloadApp',
    text: 'Download the PagerDuty Mobile App',
    url: '',
    audienceKey: 'nurture_bar_download_the_mobile_app_v_2',
    completed: false,
    modal: DOWNLOAD_APP_MODAL,
    buttonText: 'Get the App',
  },
  addUsers: {
    id: 'addUsers',
    text: 'Invite your team to PagerDuty',
    url: '/users',
    audienceKey: 'nurture_bar_add_users_to_your_account',
    completed: false,
    modal: ADD_USERS_MODAL,
    buttonText: 'Invite Team',
  },
  contactMethods: {
    id: 'contactMethods',
    text: 'Set up Your User Profile',
    url: '/users',
    audienceKey: '',
    completed: false,
    modal: CONTACT_METHODS_MODAL,
    buttonText: 'View It Now'
  },
  manageOnCall: {
    id: 'manageOnCall',
    text: 'Managing Your On-Call Schedules',
    url: '',
    audienceKey: 'nurture_bar_seen_managing_on_call_shifts',
    completed: false,
    modal: MANAGE_ON_CALL_MODAL,
    buttonText: 'View It Now',
  },
  ackAndOwnIncidents: {
    id: 'ackAndOwnIncidents',
    text: 'Learn About Acking & Owning Incidents',
    url: '',
    audienceKey: 'nurture_bar_seen_acking_and_owning',
    completed: false,
    modal: ACK_AND_OWN_INCIDENTS_MODAL,
    buttonText: 'View It Now',
  }
};

export default NURTURE_STEPS;
